import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Input } from 'modules/ui/atoms/Input'
import Label from 'modules/ui/atoms/Typography/Label'
import { DEFAULT_FOCUS_STYLES, DEFAULT_THEME_COLORS, getBodyFonts } from 'css/theme'
import TextPlaceloader from 'modules/ui/atoms/Loaders/TextPlaceloader'
import useDebounce from 'utils/hooks/useDebounce'
import { UiIcon } from 'modules/ui/atoms/Icon'
import { useTranslation } from 'next-i18next'

export type ScrollableListData = {
  label: string
  value: any
  isHeader?: boolean
}

export interface ScrollableListProps {
  /** An array of data to be displayed as List options. */
  data: ScrollableListData[]
  /** Value preselected from the list */
  selected?: string | number
  /** Height of the list */
  height?: number
  /** Whether the list has more data or not */
  hasMore?: boolean
  /** Whether the list to be displayed as popup */
  isPopUpResult?: boolean
  /** Whether the list should show only on searching */
  isActivatedSearch?: boolean
  /** Whether the list is still loading */
  loading?: boolean
  /** Whether the list is has errors */
  error?: boolean
  /** Method to fetch new data using query and page number */
  fetchNewData: (query: string, pageNumber: number) => void
  /** Method to propogate back the selected value from the list */
  onSelect: (selected: ScrollableListData) => void
  /** Method to propogate back is value s being typed */
  OnSearchActive?: (value: boolean) => void
  dataId?: string
  autoFocus?: boolean
}

const ScrollableListContainer = styled.div`
  border: none;
  margin: 0;
  flex: 1;
`

const SearchBar = styled.div`
  padding-bottom: 2px;
`

const ListItemsContainer = styled.div<{ height: number; isPopUpResult: boolean }>`
  overflow: auto;
  padding-bottom: 20px;
  padding-top: 2px;
  border-radius: 8px;
  filter: drop-shadow(0px 4px 8px rgba(25, 29, 34, 0.1));
  max-height: ${(props) => `${props.height}px`};
  width: 100%;
  position: ${(props) => (props.isPopUpResult ? `absolute` : '')};
  background-color: ${DEFAULT_THEME_COLORS['--theme-color--neutral-100']};
  z-index: 1;
`

type StyledResultsItem = {
  selected?: boolean
  isHeader?: boolean
}

const SearchResultsItem = styled.div<StyledResultsItem & { isFocused?: boolean }>(
  ({ selected, isHeader, isFocused }) => ({
    // Base styles
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: '44px',
    height: 'auto',
    padding: '2px 36px',
    textAlign: 'center',
    wordWrap: 'break-word',
    overflowY: 'auto',
    backgroundColor: isFocused ? DEFAULT_THEME_COLORS['--theme-color--neutral-95'] : 'transparent',
    outline: isFocused ? DEFAULT_FOCUS_STYLES['--theme--focus-outline'] : 'none',
    // Selected styles
    ...(selected && {
      padding: '0px 12px',
      gap: '6px',
      backgroundColor: DEFAULT_THEME_COLORS['--theme-color--neutral-95'],
    }),
    ...(!isHeader && {
      '&:hover': {
        backgroundColor: DEFAULT_THEME_COLORS['--theme-color--neutral-90'],
        cursor: 'pointer',
      },
    }),
  }),
)

const NoDataBannerContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px 40px 0px 40px',
  gap: '4px',
})

const StyledButton = styled.div({
  color: DEFAULT_THEME_COLORS['--theme-color--brand-primary'],
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '12px',
  cursor: 'pointer',
  borderRadius: '6px',
  ...getBodyFonts('default', true),
})

/**
 * An infinte scrollable list.
 */
const ScrollableList: React.FC<ScrollableListProps> = (props: ScrollableListProps) => {
  const {
    data,
    selected,
    hasMore,
    isActivatedSearch,
    loading,
    height = 300,
    isPopUpResult = false,
    fetchNewData,
    onSelect,
    OnSearchActive,
    dataId = '',
    autoFocus,
  } = props

  const { t } = useTranslation()

  const [query, setQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const debouncedQuery = useDebounce(query, 500)
  const [cursor, setCursor] = useState(-1)
  const [isActiveSearch, setIsActiveSearch] = useState(false)
  const listContainerRef = useRef<HTMLDivElement>(null)

  const shouldShowSearch = isActivatedSearch ? isActiveSearch : true

  const noDataText = t('ScrollableList.noResultsFound', {
    defaultValue: 'No results found for "{{query}}"',
    query: debouncedQuery,
  })

  const noDataFound = !loading && data?.length <= 0

  const observer = useRef<IntersectionObserver>()

  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading) return
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPageNumber) => prevPageNumber + 1)
        }
      })

      if (node) {
        observer.current.observe(node)
      }
    },
    [loading, hasMore],
  )

  const visibleObserver = useRef<IntersectionObserver>()

  const lastElementVisibleRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading) return

      visibleObserver.current = new IntersectionObserver(
        (entries) => {
          const rect = entries[0].boundingClientRect

          if (entries[0].isIntersecting) {
            node.style.opacity = '1'
          } else {
            if (rect.top > 100) {
              // we dont have to fade out at the top, 100 is to account for searchBar
              node.style.opacity = '0.5'
            }
          }
        },
        {
          threshold: 1,
        },
      )

      if (node) {
        visibleObserver.current.observe(node)
      }
    },
    [loading, hasMore],
  )

  const onChange = (params: string) => {
    const isActive = params ? true : false

    setIsActiveSearch(isActive)
    OnSearchActive && OnSearchActive(isActive)
    setPageNumber(1)
    setQuery(params)
    setCursor(-1) // Reset cursor
  }

  const clearSearch = () => {
    setIsActiveSearch(false)
    OnSearchActive && OnSearchActive(false)
    setQuery('')
  }

  const handleClick = (value: string, label: string) => {
    if (!value) return

    onSelect({ value, label })
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor(0)
      listContainerRef.current?.focus()
    }
  }

  useEffect(() => {
    fetchNewData(debouncedQuery, pageNumber)
  }, [debouncedQuery, pageNumber])

  const handleListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCursor((prev) => (prev < data.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCursor((prev) => (prev > 0 ? prev - 1 : data.length - 1))
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()

      if (cursor >= 0 && cursor < data.length) {
        const { value, label } = data[cursor]
        handleClick(value, label)
      }
    }
  }

  const inputArgs = {
    placeholder: t('ScrollableList.searchPlaceholder', 'Search...'),
    required: true,
    leftIcon: 'search',
    rightIcon: 'clear',
    type: 'search',
  }

  return (
    <ScrollableListContainer>
      <SearchBar onKeyDown={handleSearchKeyDown}>
        <Input
          id="scrollableList__search-input"
          dataId={dataId ? `${dataId}-search-input` : ''}
          {...inputArgs}
          onChange={onChange}
          value={query}
          autoFocus={autoFocus}
        />
      </SearchBar>
      {shouldShowSearch && (
        <ListItemsContainer
          ref={listContainerRef}
          height={height}
          isPopUpResult={isPopUpResult}
          role="list-box"
          onKeyDown={handleListKeyDown}
          tabIndex={-1}
        >
          {data.map(({ label, value, isHeader }, index) => {
            const isSelected = selected === value
            const isFocused = cursor === index
            const isLast = data.length === index + 1
            const ref = isLast ? lastElementRef : null

            return (
              <div key={index} ref={ref}>
                <SearchResultsItem
                  selected={isSelected}
                  isHeader={isHeader}
                  isFocused={isFocused}
                  ref={lastElementVisibleRef}
                  onClick={() => handleClick(value, label)}
                  role="option"
                  data-id={dataId ? `${dataId}-list-item` : ''}
                >
                  {isSelected && <UiIcon name="big_check" w={18} h={18} />}
                  <Label
                    style={{
                      padding: '6px 0px',
                      textAlign: 'left',
                      fontWeight: isHeader ? 'bold' : 'inherit',
                    }}
                  >
                    {label}
                  </Label>
                </SearchResultsItem>
              </div>
            )
          })}

          {loading && (
            <>
              <SearchResultsItem>
                <TextPlaceloader />
              </SearchResultsItem>
              <SearchResultsItem>
                <TextPlaceloader />
              </SearchResultsItem>
              <SearchResultsItem>
                <TextPlaceloader />
              </SearchResultsItem>
            </>
          )}

          {noDataFound && loading && (
            <NoDataBannerContainer>
              <Label
                size="default"
                bold={true}
                color={DEFAULT_THEME_COLORS['--theme-color--neutral-10']}
              >
                {noDataText}
              </Label>

              <Label size="sm">
                {t(
                  'ScrollableList.searchSuggestion',
                  'Try searching for another program or choose one from the list',
                )}
              </Label>

              <StyledButton onClick={clearSearch}>
                {t('ScrollableList.clearSearchButton', 'Clear Search')}
              </StyledButton>
            </NoDataBannerContainer>
          )}
        </ListItemsContainer>
      )}
    </ScrollableListContainer>
  )
}

export default ScrollableList
